import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    fetchBrands,
    fetchBusinessSettings,
    createStripeCheckout,
    fetchSavedCards,
    deleteSavedCard,
    validateDiscountCode,
    fetchLastOrders,
    fetchFavourites,
} from "@/lib/api";
import { formatMoney } from "@/lib/orderUtils";
import { LoyaltyPanel, LoyaltyPromoBanner, SlotPicker } from "@/lib/checkoutExtras";
import { ModifierPicker } from "@/lib/ModifierPicker";
import { groupItemsByCategory } from "@/lib/menuGrouping";
import CategoryNav from "@/components/CategoryNav";
import { TagChips } from "@/components/TagChips";
import { BrandBadgeOverlay } from "@/components/BrandBadges";
import { computeBrandBadges } from "@/lib/brandBadges";
import { optimizedImage, optimizedSrcSet } from "@/lib/cloudinaryImage";
import { ImageLightbox } from "@/components/ImageLightbox";
import { useMemberDiscountRule, computeMemberDiscount, memberDiscountLabel } from "@/lib/memberDiscount";
import { FavouritesRail, stashPendingFavourites, readDeviceFavourites, resolveFavourites } from "@/components/FavouritesRail";
import { useCustomerAuth, GoogleSignInButton, CustomerAuthBadge, ClubVoucherChips } from "@/components/CustomerGoogleAuth";
import { brandOpenState, sortBrandsByAvailability } from "@/lib/brandAvailability";

/**
 * Hidden multi-brand QR ordering route.
 *
 * - Mobile-first unified brand grid + search.
 * - Drill into a brand's full menu (categories, modifiers, popular).
 * - Persistent cross-brand basket (localStorage) — survives "← Back to brands".
 * - Collection only. No delivery toggle. No table tracking.
 * - Reuses Stripe, Loyalty (saved cards, points), and discount-code flows.
 */

const CART_KEY = "naami_order_cart_v1";
const LAST_ORDER_KEY = "naami_last_order_v1";

// Persist a compact "last basket" snapshot so users can one-tap re-order next time
const saveLastOrder = (lines) => {
    if (!lines?.length) return;
    try {
        const snapshot = {
            saved_at: new Date().toISOString(),
            lines: lines.map((l) => ({
                item_id: l.item_id,
                brand_id: l.brand_id,
                qty: l.qty,
                // Modifiers go too so re-add restores the same combo
                modifiers: l.modifiers || [],
                _modSig: l._modSig || "",
                // For the banner preview only — actual price re-fetched from brand
                _preview_name: l.name,
                _preview_brand_token: l.brand_token,
            })),
        };
        localStorage.setItem(LAST_ORDER_KEY, JSON.stringify(snapshot));
    } catch { /* ignore quota */ }
};

const MultiBrandOrder = () => {
    const navigate = useNavigate();

    // Master data
    const [brands, setBrands] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);

    // Navigation
    const [view, setView] = useState("brands"); // 'brands' | 'menu'
    const [selectedSlug, setSelectedSlug] = useState(null);
    const [search, setSearch] = useState("");

    // Cart (persistent across brand switches)
    const [lines, setLines] = useState(() => {
        try {
            const raw = localStorage.getItem(CART_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    useEffect(() => {
        try { localStorage.setItem(CART_KEY, JSON.stringify(lines)); } catch { /* ignore */ }
    }, [lines]);

    // Modifier picker state — must know which brand the item belongs to
    const [pickerItem, setPickerItem] = useState(null);
    const [pickerBrand, setPickerBrand] = useState(null);
    const [lightboxImage, setLightboxImage] = useState(null);

    // Checkout sheet + form state
    const [showCheckout, setShowCheckout] = useState(false);
    const [customerName, setCustomerName] = useState("");
    const [notes, setNotes] = useState("");
    // Per-brand kitchen notes — keyed by brand_id. Used only when the basket
    // has more than one brand; single-brand baskets fall back to `notes`.
    const [brandNotes, setBrandNotes] = useState({});
    const [payMethod, setPayMethod] = useState("card");
    const [placing, setPlacing] = useState(false);
    const [member, setMember] = useState(null);
    const { auth: customerAuth, error: customerAuthError, signOut: customerSignOut, refresh: refreshCustomer } = useCustomerAuth();
    useEffect(() => { if (customerAuth?.member) setMember(customerAuth.member); }, [customerAuth]);
    const handleCustomerSignOut = async () => { await customerSignOut(); setMember(null); setRedeemPoints(0); };
    const setMemberOrSignOut = (m) => { if (!m && customerAuth) handleCustomerSignOut(); else setMember(m); };
    const [redeemPoints, setRedeemPoints] = useState(0);
    const [scheduledFor, setScheduledFor] = useState(null);

    // Delivery / collection state (delivery is disabled globally if
    // settings.deliveries_enabled is false — UI hides the toggle).
    const [orderType, setOrderType] = useState("collection");
    const [deliveryAddr, setDeliveryAddr] = useState({
        address_line_1: "", address_line_2: "", city: "", postcode: "", instructions: "", phone: "",
    });
    // If deliveries are globally disabled, force collection.
    useEffect(() => {
        if (settings && settings.deliveries_enabled === false && orderType === "delivery") {
            setOrderType("collection");
        }
    }, [settings, orderType]);

    // Saved cards (loyalty)
    const [savedCards, setSavedCards] = useState([]);
    const [selectedCardId, setSelectedCardId] = useState(null);

    // Discount code
    const [discountInput, setDiscountInput] = useState("");
    const [appliedDiscount, setAppliedDiscount] = useState(null);
    const [discountError, setDiscountError] = useState("");
    const [validatingCode, setValidatingCode] = useState(false);

    // Last order snapshot (for one-tap reorder banner)
    const [lastOrder, setLastOrder] = useState(() => {
        try {
            const raw = localStorage.getItem(LAST_ORDER_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const dismissLastOrder = () => {
        try { localStorage.removeItem(LAST_ORDER_KEY); } catch { /* ignore */ }
        setLastOrder(null);
    };

    // Server-side "Order again" tiles for signed-in Naami Club members
    const [memberLastOrders, setMemberLastOrders] = useState([]);
    useEffect(() => {
        if (!member?.id) {
            setMemberLastOrders([]);
            return;
        }
        fetchLastOrders(member.id, 3)
            .then((r) => setMemberLastOrders(r?.orders || []))
            .catch(() => setMemberLastOrders([]));
    }, [member?.id]);

    // "Your favourites": member order history first, else this device's past orders.
    const [memberFavRows, setMemberFavRows] = useState([]);
    useEffect(() => {
        if (!member?.id) { setMemberFavRows([]); return; }
        fetchFavourites(member.id, 6).then((r) => setMemberFavRows(r?.items || [])).catch(() => setMemberFavRows([]));
    }, [member?.id]);
    const favourites = useMemo(() => {
        const fromMember = resolveFavourites(memberFavRows, brands, 6);
        return fromMember.length ? fromMember : resolveFavourites(readDeviceFavourites(), brands, 6);
    }, [memberFavRows, brands]);

    // Load brands + business settings
    useEffect(() => {
        Promise.all([fetchBrands(), fetchBusinessSettings()])
            .then(([bs, s]) => { setBrands(bs); setSettings(s); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    // Reload saved cards when member changes
    useEffect(() => {
        if (member?.id) {
            fetchSavedCards(member.id).then((r) => setSavedCards(r.cards || [])).catch(() => setSavedCards([]));
        } else {
            setSavedCards([]);
            setSelectedCardId(null);
        }
    }, [member?.id]);

    const activeBrand = useMemo(
        () => brands.find((b) => b.slug === selectedSlug) || null,
        [brands, selectedSlug]
    );

    const filteredBrands = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return brands;
        return brands.filter((b) =>
            (b.name || "").toLowerCase().includes(q) ||
            (b.cuisine || "").toLowerCase().includes(q) ||
            (b.tagline || "").toLowerCase().includes(q)
        );
    }, [brands, search]);

    // --- Cart actions ---
    const addItem = (brand, item, mods = [], priceOverride = null) => {
        // Only fully block for coming_soon brands. Closed-for-now brands allow
        // pre-orders (customer schedules for later at checkout).
        const avail = brandOpenState(brand);
        if (!avail.available && avail.reason === "coming_soon") {
            alert(`${brand.name} is opening soon — you can't order yet.`);
            return;
        }
        if (!avail.available && avail.reason === "closed_now") {
            alert(`${brand.name} is closed right now and isn't taking orders.`);
            return;
        }
        if ((item.modifier_groups?.length || 0) > 0 && mods.length === 0 && priceOverride === null) {
            setPickerBrand(brand);
            setPickerItem(item);
            return;
        }
        const modDelta = mods.reduce((s, m) => s + (m.price_delta || 0), 0);
        const unitPrice = priceOverride !== null ? priceOverride : Math.round((item.price + modDelta) * 100) / 100;
        const sig = mods.map((m) => `${m.group_name}:${m.option_name}`).sort().join("|");
        setLines((cur) => {
            const idx = cur.findIndex((l) => l.item_id === item.id && (l._modSig || "") === sig);
            if (idx >= 0) {
                const n = [...cur];
                n[idx] = { ...n[idx], qty: n[idx].qty + 1 };
                return n;
            }
            return [
                ...cur,
                {
                    item_id: item.id, brand_id: brand.id, brand_name: brand.name,
                    brand_token: brand.token, brand_accent: brand.accent,
                    category: item.category || null,
                    name: item.name, price: unitPrice, base_price: item.price, qty: 1,
                    modifiers: mods, _modSig: sig,
                },
            ];
        });
    };

    const changeQty = (itemId, delta, modSig = null) => {
        setLines((cur) =>
            cur.map((l) =>
                l.item_id === itemId && (l._modSig || "") === (modSig || "")
                    ? { ...l, qty: l.qty + delta }
                    : l
            ).filter((l) => l.qty > 0)
        );
    };

    const clearCart = () => setLines([]);

    // Re-add lines from a saved order (localStorage snapshot or server-side
    // member history) into the current cart, using TODAY's brand/item names and
    // prices (catalogue source of truth). Items no longer available are silently
    // skipped. Returns count actually re-added.
    const reorderFromLines = (savedLines) => {
        if (!savedLines?.length) return 0;
        const brandById = Object.fromEntries(brands.map((b) => [b.id, b]));
        const newLines = [];
        let added = 0;
        let skipped = 0;
        for (const saved of savedLines) {
            const brand = brandById[saved.brand_id];
            const item = brand?.items?.find((i) => i.id === saved.item_id && i.available !== false);
            if (!brand || !item) { skipped += 1; continue; }
            const modDelta = (saved.modifiers || []).reduce((s, m) => s + (m.price_delta || 0), 0);
            const unitPrice = Math.round((item.price + modDelta) * 100) / 100;
            const sig = saved._modSig || "";
            newLines.push({
                item_id: item.id, brand_id: brand.id, brand_name: brand.name,
                brand_token: brand.token, brand_accent: brand.accent,
                category: item.category || null,
                name: item.name, price: unitPrice, base_price: item.price,
                qty: saved.qty || 1,
                modifiers: saved.modifiers || [], _modSig: sig,
            });
            added += saved.qty || 1;
        }
        if (newLines.length === 0) {
            if (skipped > 0) {
                setTimeout(() => alert("None of those items are available right now — try another favourite."), 50);
            }
            return 0;
        }
        setLines((cur) => {
            const merged = [...cur];
            for (const nl of newLines) {
                const idx = merged.findIndex((l) => l.item_id === nl.item_id && (l._modSig || "") === (nl._modSig || ""));
                if (idx >= 0) merged[idx] = { ...merged[idx], qty: merged[idx].qty + nl.qty };
                else merged.push(nl);
            }
            return merged;
        });
        if (skipped > 0) {
            setTimeout(() => alert(`${skipped} item${skipped === 1 ? "" : "s"} from that order are no longer available and were skipped.`), 50);
        }
        return added;
    };

    // --- Totals ---
    const subtotal = useMemo(
        () => Math.round(lines.reduce((s, l) => s + l.qty * l.price, 0) * 100) / 100,
        [lines]
    );
    const count = lines.reduce((s, l) => s + l.qty, 0);
    const brandsInCart = useMemo(() => new Set(lines.map((l) => l.brand_id)).size, [lines]);
    const loyaltyDiscount = Math.min((redeemPoints || 0) * 0.05, subtotal);

    const smallOrderFee = useMemo(() => {
        // Flat small-order fee on delivery baskets under threshold.
        if (orderType !== "delivery") return 0;
        if (!settings || !settings.small_order_threshold || !settings.small_order_fee) return 0;
        return subtotal > 0 && subtotal < settings.small_order_threshold
            ? Number(settings.small_order_fee || 0)
            : 0;
    }, [subtotal, settings, orderType]);

    // Live-computed delivery fee — null=N/A, -1=out of zone, >=0=fee
    const deliveryFee = useMemo(() => {
        if (orderType !== "delivery" || !settings) return null;
        const pc = (deliveryAddr.postcode || "").toUpperCase().replace(/\s+/g, "");
        if (!pc) return null;
        let best = null;
        for (const z of settings.delivery_zones || []) {
            const prefix = (z.postcode_prefix || "").toUpperCase().replace(/\s+/g, "");
            if (prefix && pc.startsWith(prefix)) {
                if (!best || prefix.length > best.prefix.length) best = { prefix, fee: z.fee };
            }
        }
        return best ? best.fee : -1;
    }, [orderType, deliveryAddr.postcode, settings]);

    const serviceFee = useMemo(() => {
        if (!settings || subtotal <= 0) return 0;
        const pct = Number(settings.service_fee_pct || 10);
        const floor = Number(settings.service_fee_min || 0.99);
        const ceil = Number(settings.service_fee_max || 2.49);
        const raw = Math.round((subtotal * pct / 100) * 100) / 100;
        return Math.min(ceil, Math.max(floor, raw));
    }, [subtotal, settings]);

    const bagCharge = settings ? Number(settings.bag_charge || 0) : 0;
    // Recompute from the code's rule so the amount tracks basket changes (server does the same).
    const codeDiscount = !appliedDiscount ? 0 : appliedDiscount.type === "percent"
        ? Math.round(subtotal * Number(appliedDiscount.value)) / 100
        : Math.min(Number(appliedDiscount.value || 0), subtotal);

    // Auto-applied per-brand badges (preview only — backend recomputes)
    const badgeResult = useMemo(
        () => computeBrandBadges({ lines, brands, orderType }),
        [lines, brands, orderType]
    );
    const badgeDiscount = badgeResult.discount;
    const effectiveDeliveryFee = orderType === "delivery" && deliveryFee != null && deliveryFee >= 0 ? deliveryFee : 0;
    const deliveryAfterBadge = badgeResult.freeDelivery ? 0 : effectiveDeliveryFee;

    const memberRule = useMemberDiscountRule();
    const memberDiscount = computeMemberDiscount(memberRule, { member, hasCode: !!appliedDiscount, orderType, subtotal });
    const totalDiscount = Math.min(loyaltyDiscount + codeDiscount + memberDiscount + badgeDiscount, subtotal);
    const finalTotal = Math.max(
        0,
        Math.round((subtotal - totalDiscount + deliveryAfterBadge + smallOrderFee + serviceFee + bagCharge) * 100) / 100
    );

    const deliveriesEnabled = !!(settings?.deliveries_enabled ?? true);
    const deliveryReady = orderType === "collection" || (
        deliveryFee != null && deliveryFee >= 0 &&
        deliveryAddr.address_line_1.trim() && deliveryAddr.postcode.trim() && deliveryAddr.phone.trim()
    );

    const applyDiscount = async (override) => {
        const code = (typeof override === "string" ? override : discountInput || "").trim().toUpperCase();
        if (!code) return;
        setValidatingCode(true);
        setDiscountError("");
        try {
            const r = await validateDiscountCode(code, subtotal, orderType);
            if (r.valid) { setAppliedDiscount(r); setDiscountError(""); }
            else { setAppliedDiscount(null); setDiscountError(r.reason || "Invalid code"); }
        } catch {
            setDiscountError("Could not check code");
        } finally { setValidatingCode(false); }
    };

    // Collection-only Club codes must be re-checked if the customer switches to delivery.
    useEffect(() => {
        if (appliedDiscount?.collection_only && orderType !== "collection") {
            setAppliedDiscount(null);
            setDiscountError("Naami Club codes are for collection orders only");
        }
    }, [orderType, appliedDiscount]);

    const clearDiscount = () => {
        setAppliedDiscount(null);
        setDiscountInput("");
        setDiscountError("");
    };

    const openBrand = (slug) => {
        setSelectedSlug(slug);
        setView("menu");
        window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    };

    const backToBrands = () => {
        setView("brands");
        setSelectedSlug(null);
        window.scrollTo({ top: 0 });
    };

    // Group lines by brand for the checkout summary
    const linesByBrand = useMemo(() => {
        const map = {};
        for (const l of lines) {
            if (!map[l.brand_id]) {
                map[l.brand_id] = {
                    brand_id: l.brand_id, brand_name: l.brand_name,
                    brand_token: l.brand_token, brand_accent: l.brand_accent,
                    lines: [], subtotal: 0,
                };
            }
            map[l.brand_id].lines.push(l);
            map[l.brand_id].subtotal += l.qty * l.price;
        }
        return Object.values(map);
    }, [lines]);

    const placeOrder = async () => {
        if (!lines.length) return;
        if (orderType === "delivery" && !deliveryReady) {
            alert("Please complete the delivery address before placing the order.");
            return;
        }
        if (!scheduledFor) {
            const closed = brands.filter((b) => lines.some((l) => l.brand_id === b.id) && !brandOpenState(b).available);
            if (closed.length) {
                alert(`${closed.map((b) => b.name).join(", ")} ${closed.length > 1 ? "are" : "is"} closed right now. Remove ${closed.length > 1 ? "them" : "it"} from your basket or schedule a pre-order for when ${closed.length > 1 ? "they're" : "it's"} open.`);
                return;
            }
        }
        setPlacing(true);
        // If the basket has multiple brands, send per-brand notes; single-brand
        // baskets use the global `notes` textarea.
        const multiBrand = brandsInCart > 1;
        const draft = {
            source: "qr",
            lines,
            customer_name: customerName || null,
            notes: multiBrand ? null : (notes || null),
            brand_notes: multiBrand
                ? Object.fromEntries(
                      Object.entries(brandNotes).filter(([, v]) => (v || "").trim())
                  )
                : {},
            pay_method: "card",  // Online orders always via Stripe — no cash path
            loyalty_member_id: member?.id || null,
            redeem_points: redeemPoints || 0,
            scheduled_for: scheduledFor || null,
            order_type: orderType,
            delivery: orderType === "delivery" ? deliveryAddr : null,
            discount_code: appliedDiscount?.code || null,
        };
        try {
            // ALWAYS route through Stripe — no cash path online.
            const session = await createStripeCheckout(draft, selectedCardId || null);
            if (session?.url) {
                // Snapshot the basket for one-tap reorder on return visits.
                // Cart is cleared because user is leaving for Stripe-hosted page.
                saveLastOrder(lines);
                stashPendingFavourites(lines);
                localStorage.removeItem(CART_KEY);
                window.location.href = session.url;
                return;
            }
            throw new Error("Could not start payment");
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.detail || "Failed to place order. Try again.");
        } finally { setPlacing(false); }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[var(--bg)] text-muted grid place-items-center" data-testid="order-loading">
                Loading…
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] pb-32" data-testid="multi-brand-order-page">
            {/* Discreet staff shortcut — invisible-ish to customers, one tap to the Hub tickets screen. */}
            <a
                href="/hub/login"
                className="fixed bottom-3 right-3 z-40 font-mono text-[9px] uppercase tracking-[0.22em] text-muted/40 hover:text-gold px-2 py-1 border border-transparent hover:border-[var(--border)] bg-[var(--bg)]/70 backdrop-blur-sm"
                data-testid="staff-shortcut-link"
            >
                · staff
            </a>
            {view === "brands" ? (
                <BrandsView
                    brands={filteredBrands}
                    totalBrands={brands.length}
                    search={search}
                    setSearch={setSearch}
                    onOpen={openBrand}
                    lastOrder={lastOrder}
                    cartIsEmpty={lines.length === 0}
                    onReorder={() => {
                        const n = reorderFromLines(lastOrder?.lines);
                        if (n > 0) window.scrollTo({ top: 0 });
                    }}
                    onDismissLastOrder={dismissLastOrder}
                    member={member}
                    customerAuth={customerAuth}
                    customerAuthError={customerAuthError}
                    onCustomerSignOut={handleCustomerSignOut}
                    memberLastOrders={memberLastOrders}
                    favourites={favourites}
                    onAddFavourite={addItem}
                    onMemberReorder={(order) => {
                        const n = reorderFromLines(order?.lines);
                        if (n > 0) window.scrollTo({ top: 0 });
                    }}
                />
            ) : (
                <MenuView
                    brand={activeBrand}
                    lines={lines}
                    onBack={backToBrands}
                    onAdd={(item) => addItem(activeBrand, item)}
                    favouriteIds={favourites.filter((f) => f.brand.id === activeBrand?.id).map((f) => f.item.id)}
                    onInc={(itemId, modSig) => changeQty(itemId, 1, modSig)}
                    onDec={(itemId, modSig) => changeQty(itemId, -1, modSig)}
                    onImageClick={(item) => setLightboxImage({ src: item.image_url, alt: item.name })}
                />
            )}

            {/* Floating cart bar */}
            {count > 0 && !showCheckout && (
                <button
                    onClick={() => { setShowCheckout(true); if (customerAuth) refreshCustomer(); }}
                    data-testid="order-cart-bar"
                    className="fixed bottom-0 left-0 right-0 bg-[var(--gold)] text-[var(--bg)] pt-4 px-6 pb-safe-4 flex items-center justify-between font-mono uppercase tracking-[0.18em] text-sm z-30 font-bold"
                >
                    <span>
                        {count} {count === 1 ? "item" : "items"}
                        {brandsInCart > 1 && ` · ${brandsInCart} brands`}
                        {" · View basket"}
                    </span>
                    <span>{formatMoney(subtotal)} →</span>
                </button>
            )}

            {/* Modifier picker */}
            {pickerItem && (
                <ModifierPicker
                    item={pickerItem}
                    onCancel={() => { setPickerItem(null); setPickerBrand(null); }}
                    onConfirm={(mods, totalPrice) => {
                        addItem(pickerBrand, pickerItem, mods, totalPrice);
                        setPickerItem(null);
                        setPickerBrand(null);
                    }}
                />
            )}

            {/* Image lightbox */}
            {lightboxImage && (
                <ImageLightbox
                    src={lightboxImage.src}
                    alt={lightboxImage.alt}
                    onClose={() => setLightboxImage(null)}
                />
            )}

            {/* Checkout sheet */}
            {showCheckout && (
                <CheckoutSheet
                    linesByBrand={linesByBrand}
                    lines={lines}
                    changeQty={changeQty}
                    clearCart={clearCart}
                    onClose={() => setShowCheckout(false)}
                    customerName={customerName} setCustomerName={setCustomerName}
                    notes={notes} setNotes={setNotes}
                    brandNotes={brandNotes} setBrandNotes={setBrandNotes}
                    brandsInCart={brandsInCart}
                    payMethod={payMethod} setPayMethod={setPayMethod}
                    member={member} setMember={setMemberOrSignOut}
                    redeemPoints={redeemPoints} setRedeemPoints={setRedeemPoints}
                    scheduledFor={scheduledFor} setScheduledFor={setScheduledFor}
                    savedCards={savedCards} setSavedCards={setSavedCards}
                    selectedCardId={selectedCardId} setSelectedCardId={setSelectedCardId}
                    orderType={orderType} setOrderType={setOrderType}
                    deliveryAddr={deliveryAddr} setDeliveryAddr={setDeliveryAddr}
                    deliveryFee={deliveryFee}
                    deliveriesEnabled={deliveriesEnabled}
                    subtotal={subtotal}
                    smallOrderFee={smallOrderFee}
                    serviceFee={serviceFee}
                    bagCharge={bagCharge}
                    loyaltyDiscount={loyaltyDiscount}
                    codeDiscount={codeDiscount}
                    memberDiscount={memberDiscount}
                    memberDiscountText={memberDiscountLabel(memberRule)}
                    badgeResult={badgeResult}
                    finalTotal={finalTotal}
                    settings={settings}
                    discountInput={discountInput} setDiscountInput={setDiscountInput}
                    appliedDiscount={appliedDiscount}
                    applyDiscount={applyDiscount}
                    clearDiscount={clearDiscount}
                    discountError={discountError}
                    validatingCode={validatingCode}
                    placing={placing}
                    placeOrder={placeOrder}
                    cartBrands={brands.filter((b) => lines.some((l) => l.brand_id === b.id))}
                    onBrowseMore={() => setShowCheckout(false)}
                    customerAuth={customerAuth}
                />
            )}
        </div>
    );
};

// ---------------- Member "Order again" rail (server-side history) ----------------
const MemberReorderRail = ({ member, orders, onReorder }) => {
    const fmt = (iso) => {
        try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); }
        catch { return ""; }
    };
    return (
        <div className="mb-6" data-testid="order-member-rail">
            <div className="flex items-baseline justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                    // Order again · {member.name?.split(" ")[0]}
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                    {orders.length} recent
                </span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-5 px-5 snap-x snap-mandatory">
                {orders.map((o) => {
                    const tokens = o.brand_tokens || [];
                    const previewNames = o.preview_names || [];
                    return (
                        <div
                            key={o.id}
                            className="snap-start shrink-0 w-[260px] border border-[var(--gold)] bg-[var(--gold)]/10 px-4 py-3 flex flex-col"
                            data-testid={`order-member-tile-${o.order_number}`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                                    #{o.order_number}
                                </span>
                                <span className="font-mono text-[10px] text-muted">{fmt(o.created_at)}</span>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                {tokens.slice(0, 4).map((t) => (
                                    <span key={t} className="logo-box" style={{ width: 26, height: 26, fontSize: 10 }}>{t}</span>
                                ))}
                                {tokens.length > 4 && (
                                    <span className="font-mono text-[11px] text-muted">+{tokens.length - 4}</span>
                                )}
                            </div>
                            <div className="font-mono text-[11px] text-[var(--text)]/70 mb-3 line-clamp-2 leading-tight">
                                {previewNames.join(" · ")}
                                {o.lines.length > previewNames.length && ` · +${o.lines.length - previewNames.length}`}
                            </div>
                            <div className="flex items-center justify-between mt-auto">
                                <span className="font-mono text-[12px] text-muted">
                                    {o.item_count} item{o.item_count === 1 ? "" : "s"}
                                </span>
                                <span className="font-mono text-[12px] text-gold">£{Number(o.total).toFixed(2)}</span>
                            </div>
                            <button
                                onClick={() => onReorder(o)}
                                className="btn-primary mt-3 justify-center text-[12px] py-2"
                                data-testid={`order-member-reorder-${o.order_number}`}
                            >
                                ↺ Reorder
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ---------------- Reorder banner ----------------
const LastOrderBanner = ({ lastOrder, onReorder, onDismiss }) => {
    const lines = lastOrder.lines || [];
    const itemCount = lines.reduce((s, l) => s + (l.qty || 0), 0);
    // Unique brand tokens, in order
    const tokens = [];
    const seen = new Set();
    for (const l of lines) {
        const t = l._preview_brand_token || "?";
        if (!seen.has(t)) { tokens.push(t); seen.add(t); }
    }
    const previewNames = lines.slice(0, 2).map((l) => l._preview_name).filter(Boolean);
    const moreCount = Math.max(0, lines.length - previewNames.length);
    const savedDate = (() => {
        try { return new Date(lastOrder.saved_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }); }
        catch { return ""; }
    })();

    return (
        <div
            className="border border-[var(--gold)] bg-[var(--gold)]/10 px-4 py-3 mb-5 relative"
            data-testid="order-reorder-banner"
        >
            <button
                onClick={onDismiss}
                className="absolute top-2 right-2 font-mono text-[12px] text-muted hover:text-[#ff5246] leading-none w-6 h-6 grid place-items-center"
                title="Dismiss"
                data-testid="order-reorder-dismiss"
            >
                ✕
            </button>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-1">
                // Last order {savedDate && `· ${savedDate}`}
            </div>
            <div className="flex items-center gap-2 mb-2">
                {tokens.slice(0, 4).map((t) => (
                    <span key={t} className="logo-box" style={{ width: 28, height: 28, fontSize: 11 }}>{t}</span>
                ))}
                {tokens.length > 4 && (
                    <span className="font-mono text-[11px] text-muted">+{tokens.length - 4}</span>
                )}
                <span className="font-mono text-[11px] text-muted ml-1">
                    {itemCount} item{itemCount === 1 ? "" : "s"} · {tokens.length} brand{tokens.length === 1 ? "" : "s"}
                </span>
            </div>
            {previewNames.length > 0 && (
                <div className="font-mono text-[11px] text-[var(--text)]/70 mb-3 truncate">
                    {previewNames.join(" · ")}{moreCount > 0 ? ` · +${moreCount} more` : ""}
                </div>
            )}
            <button
                onClick={onReorder}
                className="btn-primary w-full justify-center"
                data-testid="order-reorder-btn"
            >
                ↺ Re-add to basket
            </button>
        </div>
    );
};

// ---------------- Brands grid + search ----------------
const BrandsView = ({
    brands, totalBrands, search, setSearch, onOpen,
    lastOrder, cartIsEmpty, onReorder, onDismissLastOrder,
    member, memberLastOrders, onMemberReorder,
    customerAuth, customerAuthError, onCustomerSignOut,
    favourites, onAddFavourite,
}) => (
    <>
        <header className="border-b border-[var(--border)] sticky top-0 bg-[var(--bg)]/95 backdrop-blur z-20 pt-safe">
            <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
                <div className="flex items-baseline gap-3">
                    <div className="font-display text-[22px] tracking-tight">naami<span className="text-gold">.</span></div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">// Order</span>
                </div>
                {customerAuth ? (
                    <CustomerAuthBadge auth={customerAuth} onSignOut={onCustomerSignOut} />
                ) : (
                    <div className="flex items-center gap-3">
                        {member && (
                            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                                Hi {member.name?.split(" ")[0] || "club"}
                            </span>
                        )}
                        <GoogleSignInButton compact testid="header-google-signin-btn" />
                    </div>
                )}
            </div>
            {customerAuthError && (
                <div className="max-w-3xl mx-auto px-5 pb-2 font-mono text-[11px] text-[#ff5246]" data-testid="customer-auth-error">{customerAuthError}</div>
            )}
        </header>

        <section className="max-w-3xl mx-auto px-5 pt-7 pb-3">
            <p className="eyebrow">// {totalBrands} kitchens · one basket</p>
            <h1 className="font-display text-[40px] sm:text-[52px] leading-[0.95] mt-2 mb-2">
                Pick a brand,<br/><span className="text-gold">build your basket.</span>
            </h1>
            <p className="text-muted text-[14px] max-w-md font-light leading-relaxed mb-5">
                Mix and match dishes across all {totalBrands} kitchens. Pay once, collect at the counter.
            </p>

            {/* Reorder UI — preference order:
                1. Signed-in member with server-side history → horizontal "Order again" rail
                2. Guest (or member without history) with a localStorage snapshot → single banner */}
            {cartIsEmpty && member && memberLastOrders?.length > 0 ? (
                <MemberReorderRail
                    member={member}
                    orders={memberLastOrders}
                    onReorder={onMemberReorder}
                />
            ) : (
                cartIsEmpty && lastOrder?.lines?.length > 0 && (
                    <LastOrderBanner
                        lastOrder={lastOrder}
                        onReorder={onReorder}
                        onDismiss={onDismissLastOrder}
                    />
                )
            )}

            <FavouritesRail favourites={favourites} onAdd={onAddFavourite} />

            <div className="relative mb-7">
                <input
                    className="input pl-10"
                    placeholder="Search a brand or cuisine…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="order-brands-search"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[14px] text-muted pointer-events-none">⌕</span>
            </div>
        </section>

        <section className="max-w-3xl mx-auto px-5">
            {brands.length === 0 ? (
                <p className="text-muted font-mono text-[12px]">No brand matches &ldquo;{search}&rdquo;.</p>
            ) : (
                <div
                    className="grid gap-3"
                    style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}
                    data-testid="order-brands-grid"
                >
                    {sortBrandsByAvailability(brands).map((b) => {
                        const avail = brandOpenState(b);
                        const blocked = !avail.available && (avail.reason === "coming_soon" || avail.reason === "closed_now");
                        return (
                        <button
                            key={b.id}
                            onClick={() => {
                                if (blocked) {
                                    alert(avail.reason === "closed_now" ? `${b.name} is closed right now.` : `${b.name} is opening soon — check back later.`);
                                    return;
                                }
                                onOpen(b.slug);
                            }}
                            className={`text-left brand-card ${blocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                            style={{ "--brand-color": b.accent }}
                            data-testid={`order-brand-${b.slug}`}
                            aria-disabled={blocked}
                        >
                            <div className="photo" style={{ aspectRatio: "4 / 3" }}>
                                {b.image_url && <img src={optimizedImage(b.image_url, { w: 240 })} srcSet={optimizedSrcSet(b.image_url, 240)} alt={b.name} loading="lazy" style={blocked ? { filter: "grayscale(0.8)" } : undefined} />}
                                {b.halal && <span className="halal-badge">Halal</span>}
                                <BrandBadgeOverlay badges={b.badges} etaMin={b.eta_min} layout="card" orderType="collection" />
                                {!avail.available && (
                                    <div className="absolute inset-x-0 top-2 flex justify-center pointer-events-none px-2" data-testid={`order-brand-status-${b.slug}`}>
                                        <span className="font-mono text-[9px] uppercase tracking-[0.14em] px-2 py-1 bg-[var(--bg)] border border-[var(--gold)] text-gold text-center leading-tight max-w-full">
                                            {avail.reason === "coming_soon" ? "Opening soon" : avail.nextOpenLabel}
                                        </span>
                                    </div>
                                )}
                                <div className="photo-overlay" aria-hidden />
                                <div className="stripe" aria-hidden />
                            </div>
                            <div className="p-3">
                                <div className="flex items-center gap-2.5">
                                    <div className="logo-box" style={{ width: 36, height: 36, fontSize: 13 }}>{b.token}</div>
                                    <div className="min-w-0 flex-1">
                                        <h3
                                            className="font-display text-[16px] leading-tight truncate"
                                            style={{ color: b.accent }}
                                        >
                                            {b.name}
                                        </h3>
                                        <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted truncate mt-0.5">
                                            {b.cuisine}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </button>
                    );})}
                </div>
            )}
        </section>
    </>
);

// ---------------- Menu drill-in ----------------
const MenuView = ({ brand, lines, onBack, onAdd, onInc, onDec, onImageClick, favouriteIds = [] }) => {
    if (!brand) return null;
    return (
        <div style={{ "--brand-color": brand.accent }}>
            {/* Floating "Back to brands" pill — always visible on the left edge
                so customers can swap between brands with one tap.
                `top` uses env(safe-area-inset-top) so the button drops below
                the iPhone notch / status bar when installed as a home-screen
                PWA — a fixed `top-3` would land the button under the notch. */}
            <button
                onClick={onBack}
                data-testid="order-back-to-brands-floating"
                aria-label="Back to brands list"
                className="fixed z-40 flex items-center gap-1.5 left-3 sm:left-4 px-3 py-2 sm:px-4 sm:py-2.5 border border-[var(--border)] bg-[var(--bg)]/95 backdrop-blur font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] hover:text-gold hover:border-gold transition-colors shadow-lg shadow-black/40"
                style={{
                    pointerEvents: "auto",
                    top: "calc(env(safe-area-inset-top) + 0.75rem)",
                }}
            >
                <span aria-hidden>←</span>
                <span>Brands</span>
            </button>
            <div className="relative">
                {brand.image_url && (
                    <div className="h-48 sm:h-64 overflow-hidden">
                        <img src={optimizedImage(brand.image_url, { w: 800 })} srcSet={optimizedSrcSet(brand.image_url, 800)} alt={brand.name} className="w-full h-full object-cover opacity-60" />
                    </div>
                )}
                <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 0%, rgba(10,10,10,0.4) 60%, var(--bg) 100%)` }} />
                <div className="absolute top-0 right-0 p-5">
                    {brand.halal && <span className="halal-badge" style={{ position: "static" }}>Halal</span>}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-end gap-3">
                        <div className="logo-box" style={{ width: 48, height: 48, fontSize: 18 }}>{brand.token}</div>
                        <div>
                            <h1
                                className="font-display text-[28px] sm:text-[36px] leading-[0.95]"
                                style={{ color: brand.accent }}
                            >
                                {brand.name}
                            </h1>
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-1">
                                {brand.tagline}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: brand.accent }} />
            </div>

            {brand.daily_special && (
                <div className="bg-[var(--gold)] text-[var(--bg)] py-2 px-4 text-center font-mono uppercase tracking-[0.18em] text-[11px] font-bold" data-testid="order-daily-special">
                    ✦ Today: {brand.daily_special}
                </div>
            )}

            <div className="max-w-2xl mx-auto px-5 py-8">
                {(() => {
                    const grouped = groupItemsByCategory(brand.items, brand.category_order || [], brand.popular_item_ids || [], brand.category_descriptions || {});
                    const favItems = favouriteIds.map((id) => brand.items.find((i) => i.id === id && i.available !== false)).filter(Boolean);
                    if (favItems.length) grouped.unshift({ category: "Your favourites", items: favItems, isPopular: true, isFavourite: true });
                    const slug = (s) => `order-cat-${(s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
                    const navCats = grouped.map(({ category }) => ({ id: slug(category), label: category }));
                    return (
                        <>
                            <CategoryNav categories={navCats} stickyOffsetPx={0} scrollOffsetPx={70} />
                            {grouped.map(({ category, items, isPopular, isFavourite, description }) => (
                    <div key={category} id={slug(category)} className="mb-9 scroll-mt-24" data-testid={`order-cat-${category}`}>
                        <div className="mb-4 pb-3 border-b border-[var(--border)]">
                            <div className="flex items-baseline justify-between gap-3">
                                <h3 className="font-display font-bold text-[26px] sm:text-[30px] leading-tight flex items-baseline gap-2" style={{ color: isPopular ? "var(--gold)" : brand.accent }}>
                                    {isPopular && <span className="text-[16px]">{isFavourite ? "♥" : "★"}</span>}
                                    {category}
                                </h3>
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted shrink-0">
                                    {isFavourite ? "yours" : isPopular ? "7d" : items.length}
                                </span>
                            </div>
                            {description && (
                                <p className="text-[14px] sm:text-[15px] text-[var(--text)] mt-2 font-light leading-relaxed">{description}</p>
                            )}
                        </div>
                        <div className="divide-y divide-[var(--border)]">
                            {items.map((item) => {
                                const qty = lines.filter((l) => l.item_id === item.id).reduce((s, l) => s + l.qty, 0);
                                const firstLine = lines.find((l) => l.item_id === item.id);
                                return (
                                    <MenuItem
                                        key={item.id}
                                        item={item}
                                        qty={qty}
                                        onAdd={() => onAdd(item)}
                                        onInc={() => onInc(item.id, firstLine?._modSig)}
                                        onDec={() => onDec(item.id, firstLine?._modSig)}
                                        onImageClick={() => onImageClick(item)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
                        </>
                    );
                })()}
            </div>
        </div>
    );
};

const MenuItem = ({ item, qty, onAdd, onInc, onDec, onImageClick }) => {
    const hasMods = (item.modifier_groups?.length || 0) > 0;
    return (
        <div className="py-4 border-b border-[var(--border)] last:border-b-0" data-testid={`order-menu-item-${item.id}`}>
            <div className="flex gap-4">
                <div className="flex-1 min-w-0 flex flex-col">
                    <TagChips tags={[
                        ...(item.popular && !(item.tags || []).includes("best_seller") ? ["best_seller"] : []),
                        ...(item.tags || []),
                    ]} />
                    <h3 className="font-display text-[16px] sm:text-[17px] leading-snug mt-1.5 mb-1.5">{item.name}</h3>
                    {item.description && (
                        <p className="text-[13px] text-[var(--text)]/65 font-light leading-relaxed mb-2">
                            {item.description}
                        </p>
                    )}
                    <div className="flex items-center gap-3 font-mono text-[12px] mt-auto pt-1">
                        <span className="text-gold text-[14px]">{formatMoney(item.price)}</span>
                        {item.calories && <span className="text-muted">{item.calories} kcal</span>}
                    </div>
                </div>
                {item.image_url && (
                    <button
                        type="button"
                        onClick={onImageClick}
                        className="relative w-24 h-24 sm:w-28 sm:h-28 shrink-0 overflow-hidden bg-[var(--surface-elevated)] group cursor-zoom-in p-0 border-0"
                        aria-label={`Enlarge ${item.name}`}
                        data-testid={`order-menu-img-${item.id}`}
                    >
                        <img src={optimizedImage(item.image_url, { w: 120 })} srcSet={optimizedSrcSet(item.image_url, 120)} alt={item.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        <span className="absolute bottom-1 right-1 w-6 h-6 bg-black/55 text-white font-mono text-[11px] grid place-items-center" aria-hidden>⤢</span>
                    </button>
                )}
            </div>
            <div className="flex justify-end mt-3">
                {qty > 0 && !hasMods ? (
                    <div className="flex items-center gap-2">
                        <button onClick={onDec} className="w-9 h-9 border border-[var(--border-strong)] grid place-items-center" data-testid={`order-menu-dec-${item.id}`}>−</button>
                        <span className="font-mono w-6 text-center">{qty}</span>
                        <button onClick={onInc} className="w-9 h-9 border border-[var(--border-strong)] grid place-items-center" data-testid={`order-menu-inc-${item.id}`}>+</button>
                    </div>
                ) : (
                    <button onClick={onAdd} className="btn-ghost whitespace-nowrap" data-testid={`order-menu-add-${item.id}`}>
                        {hasMods ? (qty > 0 ? `+ Add (${qty})` : "+ Customise") : "+ Add"}
                    </button>
                )}
            </div>
        </div>
    );
};

// ---------------- Checkout sheet ----------------
const CheckoutSheet = ({
    linesByBrand, lines, changeQty, clearCart, onClose, onBrowseMore,
    customerName, setCustomerName, notes, setNotes,
    brandNotes, setBrandNotes, brandsInCart,
    payMethod, setPayMethod,
    member, setMember, redeemPoints, setRedeemPoints,
    scheduledFor, setScheduledFor,
    savedCards, setSavedCards, selectedCardId, setSelectedCardId,
    orderType, setOrderType,
    deliveryAddr, setDeliveryAddr, deliveryFee, deliveriesEnabled,
    subtotal, smallOrderFee, serviceFee, bagCharge,
    loyaltyDiscount, codeDiscount, memberDiscount = 0, memberDiscountText, badgeResult, finalTotal, settings,
    discountInput, setDiscountInput, appliedDiscount,
    applyDiscount, clearDiscount, discountError, validatingCode, customerAuth,
    placing, placeOrder, cartBrands = [],
}) => {
    const multiBrand = brandsInCart > 1;
    return (
    <div className="fixed inset-0 bg-[#050505] z-40 overflow-y-auto" data-testid="order-checkout-sheet">
        <div className="min-h-screen flex flex-col">
            <div className="border-b border-[var(--border)] px-5 pb-5 pt-safe-4 flex items-center justify-between sticky top-0 bg-[#050505] z-10">
                <button onClick={onClose} className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted" data-testid="order-checkout-close">
                    ← Back to menu
                </button>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">// Basket</span>
            </div>
            <div className="flex-1 max-w-2xl mx-auto w-full px-5 py-7">
                <p className="eyebrow">// Review &amp; Pay</p>
                <h2 className="font-display text-[32px] leading-[0.95] mt-3 mb-2">
                    Your <span className="text-gold">order.</span>
                </h2>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mb-6">
                    {linesByBrand.length === 1
                        ? `From ${linesByBrand[0].brand_name}`
                        : `Across ${linesByBrand.length} brands · 1 basket`}
                </p>

                {/* Items grouped by brand */}
                <div className="space-y-6 mb-6">
                    {linesByBrand.map((g) => (
                        <div key={g.brand_id} className="border border-[var(--border)]" data-testid={`order-basket-brand-${g.brand_token}`}>
                            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]" style={{ background: "var(--surface-elevated)" }}>
                                <div className="flex items-center gap-2">
                                    <span className="logo-box" style={{ width: 28, height: 28, fontSize: 11 }}>{g.brand_token}</span>
                                    <span className="font-display text-[16px]" style={{ color: g.brand_accent }}>{g.brand_name}</span>
                                </div>
                                <span className="font-mono text-[11px] text-muted">{formatMoney(g.subtotal)}</span>
                            </div>
                            <div className="divide-y divide-[var(--border)] px-4">
                                {g.lines.map((l) => (
                                    <div key={`${l.item_id}-${l._modSig || ""}`} className="py-3 flex items-center justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[14px]">{l.name}</div>
                                            {l.modifiers?.length > 0 && (
                                                <div className="font-mono text-[11px] text-gold/80 leading-tight mt-0.5">
                                                    {l.modifiers.map((m) => m.option_name).join(" · ")}
                                                </div>
                                            )}
                                            <div className="font-mono text-[11px] text-muted">{formatMoney(l.price)}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => changeQty(l.item_id, -1, l._modSig)} className="w-8 h-8 border border-[var(--border-strong)] grid place-items-center" data-testid={`order-qty-dec-${l.item_id}`}>−</button>
                                            <span className="font-mono w-5 text-center">{l.qty}</span>
                                            <button onClick={() => changeQty(l.item_id, 1, l._modSig)} className="w-8 h-8 border border-[var(--border-strong)] grid place-items-center" data-testid={`order-qty-inc-${l.item_id}`}>+</button>
                                        </div>
                                        <div className="font-mono w-16 text-right text-[12px]">{formatMoney(l.price * l.qty)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex gap-3 mb-7">
                    <button onClick={onBrowseMore} className="btn-secondary flex-1 justify-center" data-testid="order-browse-more">
                        + Browse more brands
                    </button>
                    <button onClick={clearCart} className="btn-ghost" data-testid="order-clear-basket">
                        Clear
                    </button>
                </div>

                {/* Delivery / Collection toggle — hidden when deliveries_enabled=false */}
                {deliveriesEnabled ? (
                    <>
                        <div className="grid grid-cols-2 mb-6 border-2 border-[var(--gold)]" data-testid="order-type-toggle">
                            <button
                                onClick={() => setOrderType("delivery")}
                                className={`py-4 font-display text-[20px] tracking-wide transition-colors ${
                                    orderType === "delivery"
                                        ? "bg-[#050505] text-[var(--gold)]"
                                        : "bg-transparent text-[var(--text)]/60 hover:text-[var(--text)]"
                                }`}
                                data-testid="order-type-delivery"
                            >
                                {orderType === "delivery" && "✓ "}DELIVERY
                            </button>
                            <button
                                onClick={() => setOrderType("collection")}
                                className={`py-4 font-display text-[20px] tracking-wide transition-colors ${
                                    orderType === "collection"
                                        ? "bg-[#050505] text-[var(--gold)]"
                                        : "bg-transparent text-[var(--text)]/60 hover:text-[var(--text)]"
                                }`}
                                data-testid="order-type-collection"
                            >
                                {orderType === "collection" && "✓ "}COLLECTION
                            </button>
                        </div>

                        {orderType === "delivery" && (
                            <div className="mb-6 border border-[var(--border)] p-4 space-y-3" data-testid="order-delivery-form">
                                <p className="eyebrow">// Delivery details</p>
                                <input
                                    className="input"
                                    placeholder="Postcode (e.g. EH5 2AB)"
                                    value={deliveryAddr.postcode}
                                    onChange={(e) => setDeliveryAddr({ ...deliveryAddr, postcode: e.target.value.toUpperCase() })}
                                    data-testid="order-delivery-postcode"
                                />
                                {deliveryFee != null && deliveryFee >= 0 && (
                                    <div className="font-mono text-[12px] text-gold flex justify-between" data-testid="order-delivery-fee-line">
                                        <span>✓ Delivery to your postcode</span>
                                        <span>{formatMoney(deliveryFee)}</span>
                                    </div>
                                )}
                                {deliveryFee === -1 && (
                                    <div className="font-mono text-[12px] text-[#ff5246]" data-testid="order-delivery-not-available">
                                        Sorry, we don&apos;t deliver to that postcode yet.
                                    </div>
                                )}
                                <input className="input" placeholder="Address line 1" value={deliveryAddr.address_line_1} onChange={(e) => setDeliveryAddr({ ...deliveryAddr, address_line_1: e.target.value })} data-testid="order-delivery-line1" />
                                <input className="input" placeholder="Address line 2 (optional)" value={deliveryAddr.address_line_2} onChange={(e) => setDeliveryAddr({ ...deliveryAddr, address_line_2: e.target.value })} />
                                <input className="input" placeholder="City" value={deliveryAddr.city} onChange={(e) => setDeliveryAddr({ ...deliveryAddr, city: e.target.value })} />
                                <input className="input" placeholder="Mobile (so the driver can call you)" value={deliveryAddr.phone} onChange={(e) => setDeliveryAddr({ ...deliveryAddr, phone: e.target.value })} data-testid="order-delivery-phone" />
                                <textarea className="input" rows="2" placeholder="Delivery instructions (buzzer, flat number, gate code…)" value={deliveryAddr.instructions} onChange={(e) => setDeliveryAddr({ ...deliveryAddr, instructions: e.target.value })} />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="border-2 border-[var(--gold)] py-3 px-4 mb-6 text-center font-display text-[18px] tracking-wide text-gold" data-testid="order-collection-only">
                        ✓ COLLECTION AT COUNTER
                    </div>
                )}

                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Your name (optional)</label>
                <input className="input mb-5" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="e.g. Sam" data-testid="order-name-input" />

                {!member && (
                    <div className="mb-5" data-testid="checkout-google-signin">
                        <GoogleSignInButton testid="checkout-google-signin-btn" />
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-2 text-center">
                            Sign in to load your Club points &amp; vouchers — or continue as guest
                        </p>
                    </div>
                )}

                <LoyaltyPromoBanner
                    member={member}
                    onJoinClick={() => document.querySelector('[data-testid="loyalty-join-open"]')?.click()}
                />

                <LoyaltyPanel
                    otherDiscount={codeDiscount + memberDiscount + (badgeResult?.discount || 0)}
                    member={member} setMember={setMember}
                    redeemPoints={redeemPoints} setRedeemPoints={setRedeemPoints}
                    subtotal={subtotal}
                />

                <SlotPicker
                    scheduledFor={scheduledFor}
                    setScheduledFor={setScheduledFor}
                    isOpenAt={(d) => cartBrands.every((b) => brandOpenState(b, d).available)}
                />

                {multiBrand ? (
                    <div className="mb-6" data-testid="order-brand-notes">
                        <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Notes to each kitchen (per brand)</label>
                        <p className="font-mono text-[11px] text-muted mb-3 leading-snug">
                            Each brand&apos;s slip prints separately — add allergy alerts or special instructions per kitchen so the right chef sees them.
                        </p>
                        <div className="space-y-3">
                            {linesByBrand.map((g) => (
                                <div key={g.brand_id} className="border border-[var(--border)] p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="logo-box" style={{ width: 24, height: 24, fontSize: 10 }}>{g.brand_token}</span>
                                        <span className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: g.brand_accent }}>{g.brand_name}</span>
                                    </div>
                                    <textarea
                                        className="input"
                                        rows="2"
                                        placeholder="e.g. no nuts, extra sauce on the side…"
                                        value={brandNotes[g.brand_id] || ""}
                                        onChange={(e) => setBrandNotes({ ...brandNotes, [g.brand_id]: e.target.value })}
                                        data-testid={`order-brand-note-${g.brand_token}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">Allergies / Special instructions</label>
                        <textarea className="input mb-6" rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Let the kitchen know…" data-testid="order-notes-input" />
                    </>
                )}

                {/* Payment */}
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-3">Payment</label>
                {savedCards.length > 0 && payMethod === "card" && (
                    <div className="mb-4 space-y-2" data-testid="order-saved-cards">
                        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">// Your saved cards</div>
                        {savedCards.map((c) => (
                            <div
                                key={c.id}
                                className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${selectedCardId === c.id ? "border-[var(--gold)] bg-[var(--gold)]/10" : "border-[var(--border)] hover:border-[var(--gold)]"}`}
                                onClick={() => setSelectedCardId(selectedCardId === c.id ? null : c.id)}
                                data-testid={`order-saved-card-${c.id}`}
                            >
                                <span className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ minWidth: 56 }}>
                                    {c.brand}
                                </span>
                                <span className="font-mono text-sm">•••• {c.last4}</span>
                                <span className="font-mono text-[10px] text-muted">exp {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (window.confirm(`Remove ${c.brand.toUpperCase()} •••• ${c.last4}?`)) {
                                            deleteSavedCard(c.id, member.id).then(() => fetchSavedCards(member.id).then((r) => setSavedCards(r.cards || [])));
                                        }
                                    }}
                                    className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-[#ff5246]"
                                    data-testid={`order-remove-card-${c.id}`}
                                >
                                    ✕
                                </button>
                                {selectedCardId === c.id && (
                                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">✓ Selected</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {/* Card-only payment — Apple/Google Pay are surfaced on the Stripe page */}
                <div className="mb-6 p-4 border border-[var(--gold)] bg-[var(--surface-elevated)]" data-testid="order-pay-card">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-1">Card · Apple Pay · Google Pay</div>
                    <div className="text-sm text-muted">Secure payment via Stripe — no cash accepted.</div>
                </div>

                {/* Summary + offers */}
                <div className="border-t border-[var(--border)] pt-4 mb-6">
                    <p className="eyebrow mb-3">// Savings &amp; Offers</p>
                    {appliedDiscount ? (
                        <div className="flex items-center justify-between mb-4 bg-[var(--surface-elevated)] border border-[var(--gold)] px-3 py-2" data-testid="order-applied-discount">
                            <div>
                                <div className="font-mono text-[12px] text-gold">✓ {appliedDiscount.code} applied</div>
                                <div className="font-mono text-[10px] text-muted">
                                    {appliedDiscount.type === "percent" ? `${appliedDiscount.value}% off` : `£${appliedDiscount.value.toFixed(2)} off`}
                                </div>
                            </div>
                            <button onClick={clearDiscount} className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-[#ff5246]" data-testid="order-clear-discount">Remove</button>
                        </div>
                    ) : (
                        <>
                        <ClubVoucherChips vouchers={customerAuth?.vouchers} onApply={applyDiscount} busy={validatingCode} />
                        <div className="flex gap-2 mb-4">
                            <input
                                className="input flex-1 uppercase"
                                placeholder="Enter code"
                                value={discountInput}
                                onChange={(e) => setDiscountInput(e.target.value)}
                                data-testid="order-discount-input"
                            />
                            <button
                                onClick={applyDiscount}
                                disabled={validatingCode || !discountInput.trim()}
                                className="btn-secondary disabled:opacity-40"
                                data-testid="order-apply-discount"
                            >
                                {validatingCode ? "…" : "Apply"}
                            </button>
                        </div>
                        </>
                    )}
                    {discountError && (
                        <div className="font-mono text-[11px] text-[#ff5246] mb-3" data-testid="order-discount-error">{discountError}</div>
                    )}

                    <div className="space-y-2">
                        <div className="flex justify-between font-mono text-[12px] text-muted">
                            <span>Subtotal</span><span>{formatMoney(subtotal)}</span>
                        </div>
                        {smallOrderFee > 0 && (
                            <div className="flex justify-between font-mono text-[12px] text-muted" data-testid="order-summary-small">
                                <span title={settings ? `Orders under ${formatMoney(settings.small_order_threshold)} include a small order fee` : ""}>
                                    Small order fee
                                </span>
                                <span>{formatMoney(smallOrderFee)}</span>
                            </div>
                        )}
                        {serviceFee > 0 && (
                            <div className="flex justify-between font-mono text-[12px] text-muted" data-testid="order-summary-service">
                                <span>Service fee</span><span>{formatMoney(serviceFee)}</span>
                            </div>
                        )}
                        {bagCharge > 0 && (
                            <div className="flex justify-between font-mono text-[12px] text-muted" data-testid="order-summary-bag">
                                <span>Bag</span><span>{formatMoney(bagCharge)}</span>
                            </div>
                        )}
                        {loyaltyDiscount > 0 && (
                            <div className="flex justify-between font-mono text-[12px] text-gold">
                                <span>Loyalty −{redeemPoints} pts</span><span>−{formatMoney(loyaltyDiscount)}</span>
                            </div>
                        )}
{memberDiscount > 0 && (
                            <div className="flex justify-between font-mono text-[12px] text-gold" data-testid="order-summary-member-discount">
                                <span>{memberDiscountText}</span><span>−{formatMoney(memberDiscount)}</span>
                            </div>
                        )}
                        {codeDiscount > 0 && (
                            <div className="flex justify-between font-mono text-[12px] text-gold" data-testid="order-summary-code-discount">
                                <span>Code {appliedDiscount.code}</span><span>−{formatMoney(codeDiscount)}</span>
                            </div>
                        )}
                        {badgeResult?.applied?.filter((b) => b.amount > 0).map((b, i) => (
                            <div key={i} className="flex justify-between font-mono text-[12px] text-gold" data-testid={`order-summary-badge-${b.brand_token}`}>
                                <span>✓ {b.brand_name} · {b.display}</span>
                                <span>−{formatMoney(b.amount)}</span>
                            </div>
                        ))}
                        {badgeResult?.freeDelivery && (
                            <div className="flex justify-between font-mono text-[12px] text-gold" data-testid="order-summary-free-delivery">
                                <span>✓ Free delivery applied</span><span>—</span>
                            </div>
                        )}
                        <div className="flex justify-between items-baseline pt-2 border-t border-[var(--border)]">
                            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted">Total</span>
                            <span className="font-mono text-2xl text-gold" data-testid="order-total">{formatMoney(finalTotal)}</span>
                        </div>
                    </div>
                </div>

                <button onClick={placeOrder} disabled={placing || lines.length === 0 || (orderType === "delivery" && (deliveryFee == null || deliveryFee < 0 || !deliveryAddr.address_line_1?.trim() || !deliveryAddr.postcode?.trim() || !deliveryAddr.phone?.trim()))} className="btn-primary w-full justify-center text-base py-4 disabled:opacity-40" data-testid="order-place">
                    {placing ? "Placing…" :
                        scheduledFor ? "Schedule order →" :
                        `Place ${orderType} order · ${formatMoney(finalTotal)} →`}
                </button>
            </div>
        </div>
    </div>
    );
};

export default MultiBrandOrder;
